import React from 'react'
// libs
import styled from 'styled-components'
import { useRouter } from 'hooks/useRouter'
import { change, formValueSelector } from 'redux-form'
// components
import Grid from '@mui/material/Grid2'
import Input from 'components/FormElements/Input'
import ReduxEntitySelect from 'components/FormElements/ReduxForm/ReduxEntitySelect/index'
// context
import { ActionContext, ActionCreationContext, KPIContext, KPICreationContext, ObjectiveContext } from 'context/index'
// graphql
import { PLAN_DETAILS_PLAN_ITEMS_QUERY, PLANS_SELECT_QUERY } from 'graphql/plan/queries'
// hooks
import { useDispatch, useSelector } from 'react-redux'
// types
import { IField, IPlanItem } from 'types/graphql'
// themes
import { colors } from 'themes'
// utils
import find from 'lodash/fp/find'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import pipe from 'lodash/fp/pipe'
import { safeGetOr } from 'utils/fp'
import { getValueFromOption } from 'utils/form'
import { planProps, planTreeProps } from 'components/FormElements/ReduxForm/ReduxEntitySelect/utils'

const Path = styled('div')`
  color: ${colors.gray300};
  font-size: 12px;
  margin-top: 4px;
`

interface IIncomingProps {
  field: IField
  form: string
}

const ParentRecord: React.FC<IIncomingProps> = ({ field, form }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { isCreateForm: isActionCreate } = React.useContext(ActionCreationContext) as any
  const { isCreateForm: isKPICreate } = React.useContext(KPICreationContext) as any
  const { entity: kpi, plan: kpiPlan } = React.useContext(KPIContext) as any
  const { entity: action, plan: actionPlan } = React.useContext(ActionContext) as any
  const { entity: objective, plan: objectivePlan } = React.useContext(ObjectiveContext) as any

  const formSelector = formValueSelector(form)

  const { plan, parent } = useSelector((state) => ({
    plan: formSelector(state, 'plan'),
    parent: formSelector(state, 'parent')
  }))

  const entityPlan = kpiPlan || actionPlan || objectivePlan
  const planId = plan ? getValueFromOption(plan) : null
  const entity = action || objective || kpi

  const path = pipe(
    safeGetOr<any, any, any>([], 'planItems.data'),
    find((planItem: IPlanItem) => !planItem.referenced),
    (planItem) => {
      const path = safeGetOr<any, any, any>([], 'path')(planItem)

      return path.join(' > ')
    }
  )(entity)

  const shouldShowPlanRecord = entityPlan || isActionCreate || isKPICreate
  const isCreateForm = isActionCreate || isKPICreate

  const onLinkClick = () => {
    if (isEmpty(entityPlan)) {
      return
    }

    router.push({
      pathname: `/plans/${entityPlan.companyId}/edit/${entityPlan.id}`,
      query: {
        ...(entityPlan.parentPlanId && { parentId: entityPlan.parentPlanId })
      }
    })
  }

  React.useEffect(() => {
    if (!planId && parent) {
      dispatch(change(form, 'parent', null))
    }
  }, [plan])

  return (
    <>
      {shouldShowPlanRecord &&
        (isCreateForm ? (
          <Grid>
            <ReduxEntitySelect
              isClearable
              isDisabled={!isActionCreate && !isKPICreate}
              label={'Plan'}
              name={field.name}
              query={PLANS_SELECT_QUERY}
              {...planProps}
            />
          </Grid>
        ) : (
          <Grid>
            <Input
              fullWidth
              label={'Plan'}
              name={field.name}
              onClick={onLinkClick}
              value={get(plan, 'label')}
              shrink={!isEmpty(entityPlan)}
              readOnly={!isEmpty(entityPlan)}
            />
            <Path>{path}</Path>
          </Grid>
        ))}
      {isCreateForm && (
        <Grid>
          <ReduxEntitySelect
            key={planId}
            isClearable
            isDisabled={!planId}
            label={'Parent Record in Plan'}
            name={'parent'}
            planId={planId}
            query={PLAN_DETAILS_PLAN_ITEMS_QUERY}
            skip={!planId}
            variables={{ objectiveFilter: { planId }, actionFilter: { planId }, kpiFilter: { planId } }}
            {...planTreeProps}
          />
        </Grid>
      )}
    </>
  )
}

export default ParentRecord
